import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  Public,
  DevicesOther,
  Storage,
  Backup,
  RocketLaunch,
  CloudQueue,
  CheckCircle,
  Error as ErrorIcon,
  Schedule,
  Assessment,
} from "@mui/icons-material";
import { allSites } from "../redux/slices/siteSlice";
import {
  dashboardAnalytics,
  fetchSiteAnalytics,
} from "../redux/slices/analyticsSlice";
import { SPACING } from "../theme/constants";

const Analytics = () => {
  const dispatch = useDispatch();
  const { site } = useSelector((state) => state.site);
  const sites = useMemo(() => site?.sites || [], [site?.sites]);
  const {
    analytics,
    loading: analyticsLoading,
    siteAnalytics,
    siteAnalyticsLoading,
  } = useSelector((state) => state.analytics);
  const [selectedSite, setSelectedSite] = useState("");
  const [dateRange, setDateRange] = useState("7d");

  // Fetch sites and dashboard analytics on mount
  useEffect(() => {
    dispatch(allSites());
    dispatch(dashboardAnalytics());
  }, [dispatch]);

  // Set first site as default when sites load
  useEffect(() => {
    if (sites?.length > 0 && !selectedSite) {
      setSelectedSite(sites[0].id);
    }
  }, [sites, selectedSite]);

  // Fetch site-specific analytics when selection or date range changes
  useEffect(() => {
    if (selectedSite) {
      dispatch(fetchSiteAnalytics({ siteId: selectedSite, period: dateRange }));
    }
  }, [dispatch, selectedSite, dateRange]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(dashboardAnalytics());
      if (selectedSite) {
        dispatch(
          fetchSiteAnalytics({ siteId: selectedSite, period: dateRange }),
        );
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [dispatch, selectedSite, dateRange]);

  // Get selected site details
  const currentSite = sites?.find((s) => s.id === selectedSite);
  const siteData = siteAnalytics?.data;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: SPACING.lg }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
          Analytics
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Monitor your platform and site activity in real-time
        </Typography>
      </Box>

      {/* Platform Overview – Modern border-based container */}
      <Box
        sx={{
          mb: 3,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 2.5,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Platform Overview
        </Typography>

        {analyticsLoading ? (
          <Box display="flex" justifyContent="center" py={SPACING.lg}>
            <CircularProgress />
          </Box>
        ) : analytics?.data?.overview ? (
          <>
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Box
                  sx={{
                    textAlign: "center",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Public color="primary" sx={{ fontSize: 32, mb: 1 }} />
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    color="primary.main"
                  >
                    {analytics.data.overview.totalSites || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Sites
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Box
                  sx={{
                    textAlign: "center",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <CloudQueue sx={{ fontSize: 32, mb: 1 }} color="success" />
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    color="success.main"
                  >
                    {analytics.data.overview.activeSites || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Sites
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Box
                  sx={{
                    textAlign: "center",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Backup sx={{ fontSize: 32, mb: 1, color: "warning.main" }} />
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    color="warning.main"
                  >
                    {analytics.data.overview.totalBackups || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Backups
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Box
                  sx={{
                    textAlign: "center",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <RocketLaunch
                    sx={{ fontSize: 32, mb: 1, color: "info.main" }}
                  />
                  <Typography variant="h4" fontWeight={700} color="info.main">
                    {analytics.data.overview.recentDeployments || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Deployments (30d)
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Storage Usage */}
            <Box mt={3} pt={2} borderTop="1px solid" borderColor="divider">
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Storage color="action" />
                    <Typography variant="body2">
                      <strong>Storage Used:</strong>{" "}
                      {(
                        Number(analytics.data.overview.totalStorageMB) || 0
                      ).toFixed(2)}{" "}
                      MB
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <DevicesOther color="action" />
                    <Typography variant="body2">
                      <strong>Bandwidth Used:</strong>{" "}
                      {Number(
                        analytics.data.overview.totalBandwidthGB || 0,
                      ).toFixed(2)}{" "}
                      GB
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </>
        ) : (
          <Typography color="text.secondary" textAlign="center">
            No analytics data available
          </Typography>
        )}
      </Box>

      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Site Analytics
      </Typography>

      {/* Site filters – bordered container */}
      <Box
        sx={{
          mb: 3,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 2.5,
        }}
      >
        <Grid container spacing={{ xs: 2, sm: 2, md: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Select Site</InputLabel>
              <Select
                value={selectedSite}
                label="Select Site"
                onChange={(e) => setSelectedSite(e.target.value)}
              >
                {sites?.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name} ({s.domain})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateRange}
                label="Date Range"
                onChange={(e) => setDateRange(e.target.value)}
              >
                <MenuItem value="7d">Last 7 Days</MenuItem>
                <MenuItem value="30d">Last 30 Days</MenuItem>
                <MenuItem value="90d">Last 90 Days</MenuItem>
                <MenuItem value="1y">Last Year</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Site-specific analytics */}
      {siteAnalyticsLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : siteData ? (
        <>
          {/* Key Metrics – border-based cards */}
          <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box
                sx={{
                  textAlign: "center",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 2.5,
                }}
              >
                <Backup sx={{ fontSize: 36, color: "primary.main", mb: 1 }} />
                <Typography variant="h4" fontWeight={700}>
                  {siteData.overview?.backupCount || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Backups ({dateRange})
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box
                sx={{
                  textAlign: "center",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 2.5,
                }}
              >
                <RocketLaunch
                  sx={{ fontSize: 36, color: "success.main", mb: 1 }}
                />
                <Typography variant="h4" fontWeight={700}>
                  {siteData.overview?.deploymentCount || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Deployments ({dateRange})
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box
                sx={{
                  textAlign: "center",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 2.5,
                }}
              >
                <CheckCircle sx={{ fontSize: 36, color: "info.main", mb: 1 }} />
                <Typography variant="h4" fontWeight={700}>
                  {siteData.overview?.deploymentSuccessRate || 0}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Deploy Success Rate
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box
                sx={{
                  textAlign: "center",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 2.5,
                }}
              >
                <Assessment
                  sx={{ fontSize: 36, color: "warning.main", mb: 1 }}
                />
                <Typography variant="h4" fontWeight={700}>
                  {siteData.overview?.totalActivities || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Activities ({dateRange})
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Deployment Stats */}
          {siteData.deploymentStats &&
            Object.keys(siteData.deploymentStats).length > 0 && (
              <Box
                sx={{
                  mb: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 2.5,
                }}
              >
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Deployment Status Breakdown
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
                  {Object.entries(siteData.deploymentStats).map(
                    ([status, count]) => (
                      <Chip
                        key={status}
                        icon={
                          status === "success" ? (
                            <CheckCircle />
                          ) : status === "failed" ? (
                            <ErrorIcon />
                          ) : (
                            <Schedule />
                          )
                        }
                        label={`${status}: ${count}`}
                        color={
                          status === "success"
                            ? "success"
                            : status === "failed"
                              ? "error"
                              : "default"
                        }
                        variant="outlined"
                        sx={{ textTransform: "capitalize" }}
                      />
                    ),
                  )}
                </Box>
              </Box>
            )}

          {/* Recent Activities */}
          {siteData.activities?.length > 0 && (
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 2.5,
              }}
            >
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Recent Activities
                {currentSite && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    — {currentSite.name}
                  </Typography>
                )}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {siteData.activities.slice(0, 20).map((activity) => (
                      <TableRow key={activity.id}>
                        <TableCell>
                          <Chip
                            label={activity.type}
                            size="small"
                            variant="outlined"
                            sx={{
                              textTransform: "capitalize",
                              fontSize: "0.7rem",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {activity.description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(activity.createdAt).toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </>
      ) : selectedSite ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No analytics data available for this site in the selected period.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mt: 2 }}>
          Select a site to view its analytics.
        </Alert>
      )}
    </Box>
  );
};

export default Analytics;
